import type {ReactElement, ReactNode} from 'react';
import React, {useState} from 'react';
import type {SelectedImageAsset} from '@Components/ai-chat-window/ai-chat-window.types';
import {SelectedAssetThumb} from '../selected-asset-thumb/selected-asset-thumb';
import styles from './super-user-image-context-details.module.scss';
import type {SuperUserImageContextDetailsProps} from './super-user-image-context-details.types';

const getAssetCardKey = (asset: SelectedImageAsset, index: number): string => {
  return `${asset.imagePart.type}-${index}`;
};

const getAssetReason = (asset: SelectedImageAsset): ReactElement | null => {
  const reason = asset.selectionReason?.trim() ?? '';
  if (reason.length === 0) {
    return null;
  }
  return <span className={styles.assetReason}>{reason}</span>;
};

const getAssetTag = (label: string, value: string | null | undefined): ReactElement | null => {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) {
    return null;
  }
  return (
    <span className={styles.assetTag}>
      <span className={styles.assetTagLabel}>{label}</span>
      {trimmed}
    </span>
  );
};

const getAssetTags = (asset: SelectedImageAsset): ReactElement | null => {
  const roleTag = getAssetTag('Role', asset.role);
  const shapeTag = getAssetTag('Shape', asset.assetShape);
  if (roleTag === null && shapeTag === null) {
    return null;
  }
  return (
    <div className={styles.assetTags}>
      {roleTag}
      {shapeTag}
    </div>
  );
};

const getAssetCard = (asset: SelectedImageAsset, index: number): ReactElement => {
  return (
    <div key={getAssetCardKey(asset, index)} className={styles.assetCard}>
      <SelectedAssetThumb asset={asset} />
      {getAssetTags(asset)}
      {getAssetReason(asset)}
    </div>
  );
};

export function SuperUserImageContextDetails({enhancedPrompt, context, useSmartAssetSelection, producedByAgent}: SuperUserImageContextDetailsProps): ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(false);

  const summary = context?.summary?.trim() ?? '';
  const selectedAssets = context?.selectedAssets ?? [];
  const trimmedEnhancedPrompt = enhancedPrompt?.trim() ?? '';
  const mode = context?.mode?.trim() ?? '';
  const trimmedProducedByAgent = producedByAgent?.trim() ?? '';

  const smartAssetSelectionEnabled = useSmartAssetSelection === true;
  const hasContent = trimmedEnhancedPrompt.length > 0 || summary.length > 0 || selectedAssets.length > 0 || mode.length > 0 || trimmedProducedByAgent.length > 0;

  if (!hasContent) {
    return null;
  }

  const getSummary = (): ReactNode => {
    if (summary.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Context summary</span>
        <span className={styles.sectionText}>{summary}</span>
      </div>
    );
  };

  const getEnhancedPrompt = (): ReactNode => {
    if (trimmedEnhancedPrompt.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Enhanced prompt</span>
        <span className={styles.sectionText}>{trimmedEnhancedPrompt}</span>
      </div>
    );
  };

  const getAssetsToShow = (): ReactNode => {
    if (selectedAssets.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Reference images ({selectedAssets.length})</span>
        <div className={`flexbox gap-2 ${styles.assets}`}>{selectedAssets.map(getAssetCard)}</div>
      </div>
    );
  };

  const getSmartAssetSelectionFlag = (): ReactNode => {
    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Smart asset selection</span>
        <span className={styles.sectionText}>{smartAssetSelectionEnabled ? 'On' : 'Off'}</span>
      </div>
    );
  };

  const getMode = (): ReactElement | null => {
    if (mode.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Mode</span>
        <span className={styles.sectionText}>{mode}</span>
      </div>
    );
  };

  const getProducedByAgent = (): ReactElement | null => {
    if (trimmedProducedByAgent.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Produced by agent</span>
        <span className={styles.sectionText}>{trimmedProducedByAgent}</span>
      </div>
    );
  };

  const getGenerationContext = (): ReactNode => {
    if (!isExpanded) {
      return null;
    }

    return (
      <div className={`flex-v-row gap-3 spacing-m-t-2`}>
        {getProducedByAgent()}
        {getSmartAssetSelectionFlag()}
        {getMode()}
        {getSummary()}
        {getEnhancedPrompt()}
        {getAssetsToShow()}
      </div>
    );
  };

  return (
    <div className={`radius-8 spacing-p-2 spacing-m-t-2 ${styles.container}`}>
      <button
        type="button"
        className={`_cursor-pointer flexbox flex-align-items-center spacing-p-0 gap-1 ${styles.toggle}`}
        onClick={(): void => {
          setIsExpanded((prev) => !prev);
        }}
        aria-expanded={isExpanded}
      >
        <span className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}>{'▶'}</span>
        <span>Generation Context</span>
      </button>
      {getGenerationContext()}
    </div>
  );
}
